/**
 * WP8d / #46620 — keyboard policy for the verification search palette.
 *
 * Cmd/Ctrl+K toggles search from every verification-shell route.
 * "/" opens search when the user is not typing in a field.
 * The shell search button dispatches OPEN_VERIFICATION_SEARCH_EVENT.
 *
 * Capture-phase listeners call this so the old DockBar Cmd+K handler does not
 * also fire. Do not edit dock-bar.tsx from this slice.
 */

export const OPEN_VERIFICATION_SEARCH_EVENT = "excellent:open-verification-search";
export const CLOSE_VERIFICATION_SEARCH_EVENT = "excellent:close-verification-search";

export type SearchHotkeyDecision = "open" | "close" | "ignore";

const EDITABLE_TAGS = new Set(["INPUT", "TEXTAREA", "SELECT"]);

export function isTypingInField(target: EventTarget | null): boolean {
  if (!target || typeof target !== "object") return false;
  const el = target as { tagName?: string; isContentEditable?: boolean; closest?: (sel: string) => unknown };
  const tag = typeof el.tagName === "string" ? el.tagName.toUpperCase() : "";
  if (EDITABLE_TAGS.has(tag)) return true;
  if (el.isContentEditable) return true;
  if (typeof el.closest === "function") {
    return Boolean(el.closest("input, textarea, select, [contenteditable=\"true\"], [contenteditable=true]"));
  }
  return false;
}

export type SearchHotkeyEvent = {
  key: string;
  metaKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  shiftKey?: boolean;
  repeat?: boolean;
  target?: EventTarget | null;
};

/**
 * Decide whether a keydown should open or close the verification palette.
 * Cmd/Ctrl+K toggles even when a field is focused (palette convention).
 * "/" opens only when not typing in a field and the palette is closed.
 */
export function decideSearchHotkey(
  event: SearchHotkeyEvent,
  opts: { open?: boolean } = {},
): SearchHotkeyDecision {
  if (event.repeat) return "ignore";
  const cmdK =
    (event.metaKey || event.ctrlKey) &&
    !event.altKey &&
    event.key.toLowerCase() === "k";
  if (cmdK) return opts.open ? "close" : "open";
  if (event.key === "/" && !event.metaKey && !event.ctrlKey && !event.altKey) {
    if (opts.open) return "ignore";
    if (isTypingInField(event.target ?? null)) return "ignore";
    return "open";
  }
  return "ignore";
}

export function shouldStopHotkey(decision: SearchHotkeyDecision): boolean {
  return decision !== "ignore";
}
